/**
 * Grant-based gating for the dashboard (docs/workspace-roles/design.md §10.1).
 * Only READS User.effectiveGrants, which the API has already resolved; no
 * second implementation of resolution here (design.md §10.2).
 *
 * While the enforcement waves are in flight, a surface whose wave has not
 * landed intersects its grant check with today's tier check, never replaces
 * it: a custom role must not light up a page the API would still refuse.
 */
import { useMemo } from 'react'
import { useQuery } from '@apollo/client/react'
import { Navigate, Outlet, useOutletContext } from 'react-router-dom'
import type { DocumentNode, FieldNode, OperationDefinitionNode } from 'graphql'
import {
  CATALOG_BY_KEY,
  OPERATION_AUTHZ,
  PERMISSION_CATALOG,
  requiredKeysFor,
  type GrantMap,
  type NoKeyReason,
  type OperationAuthz,
  type OperationName,
  type PermissionKey,
} from '@nickeltechnology/types'
import { PermissionScope, SelfDocument } from '../operations-types'
import Sentry from './sentry'

export type EffectiveGrants = ReadonlyMap<PermissionKey, PermissionScope>

export function grantMapOf(grants: EffectiveGrants): GrantMap {
  return Object.fromEntries(grants) as GrantMap
}

const NO_GRANTS: EffectiveGrants = new Map()

/**
 * A missing list (pre-data, or an unmocked test) fails closed. A key the
 * catalog no longer knows is dropped rather than trusted.
 */
export function grantsFromList(
  list: readonly { key: string; scope: PermissionScope }[] | null | undefined,
): EffectiveGrants {
  if (!list) return NO_GRANTS
  const grants = new Map<PermissionKey, PermissionScope>()
  for (const { key, scope } of list) {
    if (CATALOG_BY_KEY.has(key)) grants.set(key as PermissionKey, scope)
  }
  return grants
}

export function useEffectiveGrants({
  fresh = false,
}: { fresh?: boolean } = {}): EffectiveGrants {
  const { data } = useQuery(
    SelfDocument,
    fresh ? { fetchPolicy: 'cache-and-network' } : undefined,
  )
  const list = data?.user?.user?.effectiveGrants
  return useMemo(() => grantsFromList(list), [list])
}

/**
 * The member's grants, read only while `when` is true, so a page that needs
 * them in one rare state mounts no extra Self observer otherwise.
 *
 * `loaded` exists because `grantsFromList` fails closed: a reader that shows
 * the member "holds nothing" must not mistake the pre-data window for a role
 * with no grants.
 */
export function useEffectiveGrantsWhen(when: boolean): {
  grants: EffectiveGrants
  loaded: boolean
} {
  const { data } = useQuery(SelfDocument, { skip: !when })
  const list = data?.user?.user?.effectiveGrants
  return useMemo(
    () => ({ grants: grantsFromList(list), loaded: list != null }),
    [list],
  )
}

export function usePermission(key: PermissionKey): boolean {
  return useEffectiveGrants().has(key)
}

export function useWorkspaceRoleName(): string | null {
  const { data } = useQuery(SelfDocument)
  return data?.user?.user?.workspaceRole?.name ?? null
}

/**
 * The approvals surface's one grant read. Unlike the R1 default (offer the
 * control, let the API refuse), the table hides Approve / Deny from a member
 * the API would refuse, because a pending request's approvers are already
 * decided (design.md §9.1). `changeApprovalStatus` is `unkeyed`, so
 * `useOperationAccess` cannot answer this; the key is read here, in the
 * module the ratchet allows.
 *
 * Read with cache-and-network: a stale grant is harmless where the control
 * renders anyway, but here a key granted mid-session would keep Approve /
 * Deny hidden until something else refetched Self.
 */
export function useApprovalGrants(): { approveBillPayments: boolean } {
  const { data } = useQuery(SelfDocument, { fetchPolicy: 'cache-and-network' })
  const list = data?.user?.user?.effectiveGrants
  const grants = useMemo(() => grantsFromList(list), [list])
  return {
    approveBillPayments: grants.has(
      'approvals.workflows.approve_bill_payments',
    ),
  }
}

/**
 * The scope the member holds a read at — ALL, OWNED (design.md §3.3) or null
 * — from the operation's declaration, so the surface never names a key
 * (§10.5). A declaration with no key, or more than one, answers null. For
 * surfaces that read differently under a narrowed view, never for hiding.
 */
export function useOperationGrantScope(
  document: DocumentNode,
): PermissionScope | null {
  const grants = useEffectiveGrants()
  const authz = operationAuthzOf(document)
  if (!authz || !('requires' in authz) || authz.requires.length !== 1) {
    return null
  }
  return grants.get(authz.requires[0]) ?? null
}

export function operationNameOf(document: DocumentNode): OperationName {
  const operations = document.definitions.filter(
    (definition): definition is OperationDefinitionNode =>
      definition.kind === 'OperationDefinition',
  )
  if (operations.length !== 1) {
    throw new Error(
      `operationNameOf: expected one operation definition, found ${operations.length}`,
    )
  }
  const [operation] = operations
  const label = operation.name?.value ?? 'an anonymous operation'
  if (operation.operation === 'subscription') {
    throw new Error(`operationNameOf(${label}): subscriptions carry no key`)
  }
  const selections = operation.selectionSet.selections
  const fields = selections.filter(
    (selection): selection is FieldNode =>
      selection.kind === 'Field' && selection.name.value !== '__typename',
  )
  const spreads = selections.filter((selection) => selection.kind !== 'Field')
  if (fields.length !== 1 || spreads.length > 0) {
    throw new Error(
      `operationNameOf(${label}): expected exactly one root field, found ${
        fields.map((field) => field.name.value).join(', ') || 'none'
      }${spreads.length ? ' plus a fragment spread' : ''}`,
    )
  }
  const root = operation.operation === 'mutation' ? 'Mutation' : 'Query'
  return `${root}.${fields[0].name.value}`
}

/**
 * The API's declaration for the document's root field (design.md §10.5), or
 * undefined when the checked-in map is behind the schema (a document added
 * without `pnpm --filter api authz:generate`).
 */
export function operationAuthzOf(
  document: DocumentNode,
): OperationAuthz | undefined {
  return OPERATION_AUTHZ[operationNameOf(document)]
}

/**
 * Whether the signed-in member may run an operation, decided only from the
 * API's declaration.
 *
 * - `allowed`: every required key is held, or the operation needs no key
 *   (the reason rides along as `ungated`).
 * - `denied`: `missing` names the keys not held.
 * - `ungated`: the gate is something grants cannot answer (a legacy tier,
 *   staff, no session). The control keeps its own tier or staff check.
 *
 * A control reads `offered`, never `status === 'allowed'`; for `ungated` it is
 * the caller's `whenUngated`. Gating an `ungated` operation without
 * `whenUngated` would hide the control from everyone, so outside production
 * the hook throws; in production it hides and logs.
 *
 * Render rule (design.md §9.1, D1): a denied control is ABSENT and its query
 * skipped, with only the exceptions named there. Status and plan rules stay
 * AND-ed with the result.
 */
export type OperationAccess =
  | {
      status: 'allowed'
      offered: true
      requires: readonly PermissionKey[]
      ungated?: NoKeyReason
    }
  | {
      status: 'denied'
      offered: false
      requires: readonly PermissionKey[]
      missing: readonly PermissionKey[]
    }
  | {
      status: 'ungated'
      offered: boolean
      reason: NoKeyReason
    }
  | {
      status: 'unmapped'
      offered: false
    }

/**
 * Unkeyed reasons every signed-in member clears at the floor. A `carve-out`'s
 * keys sit on argument paths (`argumentRequires`), so with no path requested
 * it is every member's. A keyed read that withholds some fields is not a
 * carve-out: `allowed` there means the operation runs, not that every field
 * comes back.
 */
const EVERY_MEMBER: readonly NoKeyReason[] = [
  'no-catalog-key',
  'carve-out',
  'signed-in',
]

export function operationAccessFor(
  authz: OperationAuthz,
  grants: EffectiveGrants,
  withArguments: readonly string[] = [],
  whenUngated?: boolean,
): OperationAccess {
  if ('ungated' in authz && !EVERY_MEMBER.includes(authz.ungated)) {
    return {
      status: 'ungated',
      offered: whenUngated ?? false,
      reason: authz.ungated,
    }
  }
  const requires = requiredKeysFor(authz, withArguments)
  const missing = requires.filter((key) => !grants.has(key))
  if (missing.length) {
    return { status: 'denied', offered: false, requires, missing }
  }
  return 'ungated' in authz
    ? { status: 'allowed', offered: true, requires, ungated: authz.ungated }
    : { status: 'allowed', offered: true, requires }
}

export function useOperationAccess(
  document: DocumentNode,
  options: {
    withArguments?: readonly string[]
    whenUngated?: boolean
    /**
     * Set when the surface hides the control rather than letting the API
     * refuse (design.md §9.1): reads Self from the network on mount, as
     * useApprovalGrants does.
     */
    fresh?: boolean
  } = {},
): OperationAccess {
  const grants = useEffectiveGrants({ fresh: options.fresh })
  const name = useMemo(() => operationNameOf(document), [document])
  const argumentsKey = (options.withArguments ?? []).join(' ')
  const { whenUngated } = options
  return useMemo(
    () =>
      decideAccess(
        name,
        grants,
        argumentsKey ? argumentsKey.split(' ') : [],
        whenUngated,
      ),
    [name, grants, argumentsKey, whenUngated],
  )
}

function decideAccess(
  name: OperationName,
  grants: EffectiveGrants,
  paths: readonly string[],
  whenUngated: boolean | undefined,
): OperationAccess {
  const authz = OPERATION_AUTHZ[name]
  if (!authz) {
    fail(
      `${name} is not in OPERATION_AUTHZ — regenerate it with \`pnpm --filter api authz:generate\`; the control is hidden until then`,
    )
    return { status: 'unmapped', offered: false }
  }
  // A typo would otherwise gate on the floor alone — looser than the API.
  const carried = Object.keys(authz.argumentRequires ?? {})
  const undeclared = paths.filter((path) => !carried.includes(path))
  if (undeclared.length) {
    fail(
      `${name} carries no argument floor for ${undeclared.join(', ')} — OPERATION_AUTHZ lists [${carried.join(', ')}]; the control is hidden until the two agree`,
    )
    return { status: 'unmapped', offered: false }
  }
  const access = operationAccessFor(authz, grants, paths, whenUngated)
  if (access.status === 'ungated' && whenUngated === undefined) {
    fail(
      `${name} is ungated (${access.reason}): pass whenUngated with the control's own tier or staff check, or it hides for everyone`,
    )
  }
  return access
}

function fail(message: string): void {
  if (import.meta.env.MODE !== 'production') throw new Error(message)
  console.error(message)
  Sentry.captureMessage(message, 'error')
}

/**
 * Every banking key, derived from the catalog. The Balance nav item and route
 * block open on ANY banking key (design.md §9.1's group rule), so a
 * cards-only or statements-only role can reach the pages it does have.
 */
export const BANKING_KEYS: readonly PermissionKey[] = PERMISSION_CATALOG.filter(
  (entry) => entry.group === 'banking',
).map((entry) => entry.key)

/** `setAccountantAccess` takes either key, which OPERATION_AUTHZ cannot carry. */
export const SET_ACCOUNTANT_ACCESS_KEYS: readonly PermissionKey[] = [
  'team_org.team.change_roles',
  'team_org.team.delete_members',
]

export function useHoldsAnyGrant(keys: readonly PermissionKey[]): boolean {
  const grants = useEffectiveGrants()
  return keys.some((key) => grants.has(key))
}

type RequireGrantProps = {
  fallbackTo?: string
} & (
  | {
      /** A single key, or a list meaning any-of (design.md §9.1's group rule). */
      permission: PermissionKey | readonly PermissionKey[]
      operation?: never
      withArguments?: never
    }
  | {
      /**
       * The operation the route exists to run; the guard reads its keys from
       * OPERATION_AUTHZ (design.md §10.5). Keyed operations only.
       */
      operation: DocumentNode
      withArguments?: readonly string[]
      permission?: never
    }
)

export function RequireGrant({
  permission,
  operation,
  withArguments,
  fallbackTo = '/dashboard',
}: RequireGrantProps) {
  const outletContext = useOutletContext()
  const { data, loading, error } = useQuery(SelfDocument)
  const effective = data?.user?.user?.effectiveGrants
  const argumentsKey = (withArguments ?? []).join(' ')
  const admitted = useMemo(() => {
    const grants = grantsFromList(effective)
    return operation
      ? routeOffered(
          operation,
          grants,
          argumentsKey ? argumentsKey.split(' ') : [],
        )
      : (typeof permission === 'string' ? [permission] : permission).some(
          (key) => grants.has(key),
        )
  }, [effective, operation, argumentsKey, permission])

  if (loading && !error) {
    return null
  }

  if (!admitted) {
    return <Navigate to={fallbackTo} replace />
  }

  return <Outlet context={outletContext} />
}

function routeOffered(
  operation: DocumentNode,
  grants: EffectiveGrants,
  withArguments: readonly string[],
) {
  const name = operationNameOf(operation)
  const access = decideAccess(name, grants, withArguments, false)
  if (access.status === 'ungated') {
    fail(
      `${name} is ungated (${access.reason}) and cannot guard a route; guard on the keys or the tier the route needs`,
    )
    return false
  }
  return access.offered
}
